import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Modal,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close,
  Download,
  VisibilityOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { btnGradient, fontStack } from './auth/authStyles';

const C = {
  card: '#2E2F45',
  input: '#25263B',
  border: '#3A3B55',
  textPrimary: '#FFFFFF',
  textMuted: '#A7AEC2',
  textPlaceholder: '#6D7593',
  accentBlue: '#22D3EE',
  accentGreen: '#34D399',
};

const PERIODS = [
  { id: '30min', label: '30 min', ms: 30 * 60 * 1000 },
  { id: '1h', label: '1 h', ms: 60 * 60 * 1000 },
  { id: '7h', label: '7 h', ms: 7 * 60 * 60 * 1000 },
  { id: '24h', label: '24 h', ms: 24 * 60 * 60 * 1000 },
  { id: '48h', label: '48 h', ms: 48 * 60 * 60 * 1000 },
  { id: '7d', label: '7 j', ms: 7 * 24 * 60 * 60 * 1000 },
];

const TABLE_METRICS = {
  aquacheck: [
    { key: 'temperature', label: 'temp', width: '70px' },
    { key: 'humidity', label: 'humidité', width: '80px' },
    { key: 'ground_humidity', label: 'humidité sol', width: '100px' },
  ],
  climatrack: [
    { key: 'temperature', label: 'temp', width: '70px' },
    { key: 'humidity', label: 'humidité', width: '80px' },
    { key: 'co2', label: 'CO2', width: '70px' },
    { key: 'tvoc', label: 'TVOC', width: '70px' },
    { key: 'sound_level', label: 'son', width: '60px' },
    { key: 'pm1_0', label: 'PM1.0', width: '70px' },
    { key: 'pm2_5', label: 'PM2.5', width: '70px' },
    { key: 'pm10', label: 'PM10', width: '70px' },
  ],
  greenwatch: [
    { key: 'temperature', label: 'temp', width: '70px' },
    { key: 'humidity', label: 'humidité', width: '80px' },
    { key: 'ground_humidity', label: 'humidité sol', width: '100px' },
  ],
};

const METRIC_ORDER = [
  'temperature',
  'humidity',
  'ground_humidity',
  'co2',
  'tvoc',
  'sound_level',
  'pm1_0',
  'pm2_5',
  'pm10',
];

const getPreviewMetrics = (devices = []) => {
  const byKey = new Map();
  const tables = [...new Set(devices.map((d) => String(d.table || '').toLowerCase()).filter(Boolean))];
  const sourceTables = tables.length ? tables : ['climatrack'];

  sourceTables.forEach((table) => {
    (TABLE_METRICS[table] || TABLE_METRICS.climatrack).forEach((metric) => {
      if (!byKey.has(metric.key)) byKey.set(metric.key, metric);
    });
  });

  return METRIC_ORDER.map((key) => byKey.get(key)).filter(Boolean);
};

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return Math.abs(n) >= 100 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
};

const toLocalInputValue = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseLocalInputValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const rangeFromPeriod = (periodId) => {
  const preset = PERIODS.find((p) => p.id === periodId) || PERIODS[3];
  const end = new Date();
  const start = new Date(end.getTime() - preset.ms);
  return {
    start: toLocalInputValue(start),
    end: toLocalInputValue(end),
  };
};

const formatPreviewTs = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const downloadBlob = (content, fileName, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

const rowsToCSV = (rows) => {
  const fieldSet = new Set(['timestamp', 'deviceName', 'deviceId', 'table']);
  rows.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (!['deviceName', 'deviceId', 'table'].includes(k)) fieldSet.add(k);
    });
  });
  const fields = Array.from(fieldSet);
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [fields.join(',')];
  rows.forEach((row) => {
    lines.push(fields.map((f) => escape(row[f])).join(','));
  });
  return lines.join('\n');
};

const rowsToJSON = (rows) => JSON.stringify(rows, null, 2);

const ExportDataModal = ({ open, onClose, devices = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const [period, setPeriod] = useState('24h');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [format, setFormat] = useState('csv');
  const [previewRows, setPreviewRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [hasPreviewed, setHasPreviewed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPeriod('24h');
    setFormat('csv');
    setPreviewRows([]);
    setTotalCount(0);
    setError('');
    setHasPreviewed(false);
    const initialRange = rangeFromPeriod('24h');
    setCustomStart(initialRange.start);
    setCustomEnd(initialRange.end);
  }, [open]);

  const applyPeriod = (periodId) => {
    const range = rangeFromPeriod(periodId);
    setPeriod(periodId);
    setCustomStart(range.start);
    setCustomEnd(range.end);
    setHasPreviewed(false);
  };

  const getRange = useCallback(() => {
    const start = parseLocalInputValue(customStart);
    const end = parseLocalInputValue(customEnd);
    return { start, end };
  }, [customStart, customEnd]);

  const periodLabel = useMemo(() => {
    if (!period) return 'dates précises';
    return PERIODS.find((p) => p.id === period)?.label || '24 h';
  }, [period]);

  const deviceSummary = useMemo(() => {
    if (!devices.length) return 'Aucun boîtier';
    if (devices.length <= 3) return devices.map((d) => d.name).join(', ');
    return `${devices.slice(0, 2).map((d) => d.name).join(', ')} +${devices.length - 2}`;
  }, [devices]);

  const previewMetrics = useMemo(() => getPreviewMetrics(devices), [devices]);

  const previewGridTemplate = useMemo(() => {
    const metricCols = previewMetrics.map((m) => m.width).join(' ');
    return `150px 120px ${metricCols}`.trim();
  }, [previewMetrics]);

  const previewMinWidth = useMemo(() => {
    const metricWidth = previewMetrics.reduce((sum, m) => sum + parseInt(m.width, 10), 0);
    return 150 + 120 + metricWidth + 48;
  }, [previewMetrics]);

  const fetchExportRows = useCallback(async () => {
    const { start, end } = getRange();
    if (!start || !end) {
      throw new Error('Veuillez indiquer une date de début et une date de fin.');
    }
    if (start > end) {
      throw new Error('La date de début doit être antérieure à la date de fin.');
    }
    if (!devices.length) {
      throw new Error('Aucun boîtier sélectionné.');
    }

    const token = localStorage.getItem('token');
    const batches = await Promise.all(
      devices.map(async (device) => {
        const response = await axios.get(
          `${API_URL}/api/data/${device.table}/${device.id}/filter`,
          {
            params: {
              startTime: start.toISOString(),
              endTime: end.toISOString(),
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        return (response.data || []).map((row) => ({
          ...row,
          deviceId: device.id,
          deviceName: device.name,
          table: device.table,
        }));
      })
    );

    return batches
      .flat()
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [API_URL, devices, getRange]);

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchExportRows();
      setTotalCount(rows.length);
      setPreviewRows(rows.slice(0, 5));
      setHasPreviewed(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Impossible de prévisualiser.');
      setPreviewRows([]);
      setTotalCount(0);
      setHasPreviewed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const rows = await fetchExportRows();
      if (!rows.length) {
        throw new Error('Aucune donnée à télécharger pour cette période.');
      }
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === 'json') {
        downloadBlob(rowsToJSON(rows), `ecowatch_export_${stamp}.json`, 'application/json');
      } else {
        downloadBlob(rowsToCSV(rows), `ecowatch_export_${stamp}.csv`, 'text/csv;charset=utf-8;');
      }
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Impossible de télécharger.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 1400 }}>
      <Box
        sx={{
          position: { xs: 'fixed', md: 'absolute' },
          top: { xs: 0, md: '50%' },
          left: { xs: 0, md: '50%' },
          right: { xs: 0, md: 'auto' },
          bottom: { xs: 0, md: 'auto' },
          transform: { xs: 'none', md: 'translate(-50%, -50%)' },
          width: { xs: '100%', md: 820 },
          maxHeight: { xs: '100%', md: '90vh' },
          height: { xs: '100%', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: C.card,
          borderRadius: { xs: 0, md: '18px' },
          border: { xs: 'none', md: `1px solid ${C.border}` },
          boxShadow: { xs: 'none', md: '0 20px 60px rgba(0,0,0,0.45)' },
          fontFamily: fontStack,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* En-tête */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            px: { xs: '16px', md: '22px' },
            py: '16px',
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                flexShrink: 0,
                backgroundColor: 'rgba(52,211,153,0.13)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Download sx={{ fontSize: 18, color: C.accentGreen }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary }}>
                Exporter les données
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: C.textMuted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {devices.length} boîtier{devices.length > 1 ? 's' : ''} · {deviceSummary}
              </Typography>
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              cursor: 'pointer',
              backgroundColor: C.input,
              border: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.textMuted,
              flexShrink: 0,
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </Box>
        </Box>

        {/* Corps */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: '16px', md: '22px' },
            py: '18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: C.textPrimary, letterSpacing: '0.6px' }}>
            Période
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {PERIODS.filter((p) => !(isMobile && p.id === '7d')).map((p) => {
              const on = period === p.id;
              return (
                <Box
                  key={p.id}
                  component="button"
                  type="button"
                  onClick={() => applyPeriod(p.id)}
                  sx={{
                    borderRadius: '8px',
                    px: '12px',
                    py: '7px',
                    cursor: 'pointer',
                    border: `1px solid ${on ? C.accentBlue : C.border}`,
                    backgroundColor: on ? 'rgba(34,211,238,0.15)' : C.input,
                    color: on ? C.accentBlue : C.textMuted,
                    fontSize: '12px',
                    fontWeight: on ? 700 : 500,
                  }}
                >
                  {p.label}
                </Box>
              );
            })}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: '12px',
            }}
          >
            <TextField
              label="Début"
              type="datetime-local"
              size="small"
              value={customStart}
              onChange={(e) => {
                setCustomStart(e.target.value);
                setPeriod(null);
                setHasPreviewed(false);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: C.input,
                  color: C.textPrimary,
                  borderRadius: '8px',
                },

                '& .MuiInputBase-input': {
                  color: C.textPrimary,
                },

                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: C.border,
                },

                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(52, 211, 153, 0.55)',
                },

                '& .MuiInputLabel-root': {
                  color: C.textMuted,
                },

                '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                  filter:
                    'invert(69%) sepia(38%) saturate(706%) hue-rotate(104deg) brightness(92%) contrast(88%)',
                  opacity: 1,
                  cursor: 'pointer',
                },
              }}
            />
            <TextField
              label="Fin"
              type="datetime-local"
              size="small"
              value={customEnd}
              onChange={(e) => {
                setCustomEnd(e.target.value);
                setPeriod(null);
                setHasPreviewed(false);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: C.input,
                  color: C.textPrimary,
                  borderRadius: '8px',
                },

                '& .MuiInputBase-input': {
                  color: C.textPrimary,
                },

                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: C.border,
                },

                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(52, 211, 153, 0.55)',
                },

                '& .MuiInputLabel-root': {
                  color: C.textMuted,
                },

                '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                  filter:
                    'invert(69%) sepia(38%) saturate(706%) hue-rotate(104deg) brightness(92%) contrast(88%)',
                  opacity: 1,
                  cursor: 'pointer',
                },
              }}
            />
          </Box>

          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: C.textPrimary, letterSpacing: '0.6px' }}>
            Format d&apos;export
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '4px',
              p: '4px',
              borderRadius: '10px',
              backgroundColor: C.input,
              border: `1px solid ${C.border}`,
            }}
          >
            {['csv', 'json'].map((f) => {
              const on = format === f;
              return (
                <Box
                  key={f}
                  component="button"
                  type="button"
                  onClick={() => setFormat(f)}
                  sx={{
                    flex: 1,
                    borderRadius: '8px',
                    py: '10px',
                    cursor: 'pointer',
                    border: 'none',
                    backgroundColor: on ? C.card : 'transparent',
                    color: on ? C.textPrimary : C.textMuted,
                    fontSize: '13px',
                    fontWeight: on ? 700 : 500,
                    textTransform: 'uppercase',
                  }}
                >
                  {f}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: C.textPrimary, letterSpacing: '0.6px' }}>
              Prévisualisation
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={handlePreview}
              disabled={loading || !devices.length}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                borderRadius: '8px',
                px: '12px',
                py: '7px',
                cursor: loading ? 'wait' : 'pointer',
                opacity: !devices.length ? 0.5 : 1,
                border: `1px solid ${C.accentBlue}`,
                backgroundColor: C.input,
                color: C.accentBlue,
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {loading ? <CircularProgress size={14} sx={{ color: C.accentBlue }} /> : <VisibilityOutlined sx={{ fontSize: 14 }} />}
              Prévisualiser
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: '12px',
              backgroundColor: C.input,
              border: `1px solid ${C.border}`,
              overflow: 'hidden',
              minHeight: 160,
            }}
          >
            {error && (
              <Typography sx={{ color: '#FF5722', fontSize: '12px', p: '14px' }}>{error}</Typography>
            )}

            {!error && !hasPreviewed && !loading && (
              <Typography sx={{ color: C.textMuted, fontSize: '12px', p: '24px', textAlign: 'center' }}>
                Cliquez sur « Prévisualiser » pour voir un aperçu des données.
              </Typography>
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={28} sx={{ color: C.accentBlue }} />
              </Box>
            )}

            {!loading && hasPreviewed && previewRows.length === 0 && (
              <Typography sx={{ color: C.textMuted, fontSize: '12px', p: '24px', textAlign: 'center' }}>
                Aucune donnée pour cette période.
              </Typography>
            )}

            {!loading && previewRows.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: previewGridTemplate,
                    gap: 0,
                    px: '12px',
                    py: '10px',
                    backgroundColor: '#25263B',
                    borderBottom: `1px solid ${C.border}`,
                    minWidth: previewMinWidth,
                  }}
                >
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: C.textPlaceholder }}>
                    timestamp
                  </Typography>
                  <Typography sx={{ fontSize: '11px', fontWeight: 700, color: C.textPlaceholder }}>
                    boîtier
                  </Typography>
                  {previewMetrics.map((metric) => (
                    <Typography key={metric.key} sx={{ fontSize: '11px', fontWeight: 700, color: C.textPlaceholder }}>
                      {metric.label}
                    </Typography>
                  ))}
                </Box>
                {previewRows.map((row, idx) => (
                  <Box
                    key={`${row.deviceId}-${row.timestamp}-${idx}`}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: previewGridTemplate,
                      px: '12px',
                      py: '9px',
                      borderBottom: `1px solid ${C.border}`,
                      minWidth: previewMinWidth,
                    }}
                  >
                    <Typography sx={{ fontSize: '11px', color: C.textPrimary }}>
                      {formatPreviewTs(row.timestamp)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: C.textPrimary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.deviceName}
                    </Typography>
                    {previewMetrics.map((metric) => (
                      <Typography key={metric.key} sx={{ fontSize: '11px', color: C.textPrimary }}>
                        {formatPreviewValue(row[metric.key])}
                      </Typography>
                    ))}
                  </Box>
                ))}
                {totalCount > previewRows.length && (
                  <Typography sx={{ fontSize: '11px', color: C.textMuted, p: '12px' }}>
                    … + {(totalCount - previewRows.length).toLocaleString('fr-FR')} lignes
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Pied */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: '10px',
            px: { xs: '16px', md: '22px' },
            py: '14px',
            pb: { xs: 'max(14px, env(safe-area-inset-bottom))', md: '14px' },
            borderTop: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '12px', color: C.textMuted, textAlign: { xs: 'center', sm: 'left' } }}>
            Période : {periodLabel} · Format : {format.toUpperCase()}
          </Typography>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                flex: { xs: 1, sm: 'none' },
                borderRadius: '10px',
                px: '16px',
                py: '9px',
                cursor: 'pointer',
                backgroundColor: C.input,
                border: `1px solid ${C.border}`,
                color: C.textMuted,
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              Annuler
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleDownload}
              disabled={downloading || !devices.length}
              sx={{
                flex: { xs: 1, sm: 'none' },
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                borderRadius: '10px',
                px: '18px',
                py: '9px',
                cursor: downloading ? 'wait' : 'pointer',
                opacity: !devices.length ? 0.5 : 1,
                border: 'none',
                background: btnGradient,
                color: '#0B1320',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              {downloading ? <CircularProgress size={14} sx={{ color: '#0B1320' }} /> : <Download sx={{ fontSize: 14 }} />}
              Télécharger
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ExportDataModal;
